import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    ClosePeriod, ComplianceTask, FundingCheck, LoadDefaultsResult, Obligation, ObligationRequest, RemittanceKind,
    RemittanceSuggestion, SalaryTaxDeposit, SalaryTaxDepositRequest, SalaryTaxRegisterRow, SalaryTaxSuggestion,
    StatutoryRemittance, StatutoryRemittanceRequest, TaskStatus, TemplateTask, TemplateTaskRequest,
} from '../interfaces/compliance.interface';

const headers = () => ({ headers: AuthService.getAuthHeader() });

// One service file for the compliance domain (W3-R).
export const ComplianceService = {
    // ---- salary-TDS deposits
    listSalaryTaxDeposits: async (institutionId: string, year?: number): Promise<SalaryTaxDeposit[]> =>
        (await axios.get(ENDPOINTS.SALARY_TAX_DEPOSIT.LIST(institutionId, year), headers())).data,
    salaryTaxRegister: async (institutionId: string, year: number): Promise<SalaryTaxRegisterRow[]> =>
        (await axios.get(ENDPOINTS.SALARY_TAX_DEPOSIT.REGISTER(institutionId, year), headers())).data,
    salaryTaxSuggestion: async (institutionId: string, year: number, month: number): Promise<SalaryTaxSuggestion> =>
        (await axios.get(ENDPOINTS.SALARY_TAX_DEPOSIT.SUGGESTION(institutionId, year, month), headers())).data,
    createSalaryTaxDeposit: async (institutionId: string, req: SalaryTaxDepositRequest): Promise<SalaryTaxDeposit> =>
        (await axios.post(ENDPOINTS.SALARY_TAX_DEPOSIT.CREATE(institutionId), req, headers())).data,
    reverseSalaryTaxDeposit: async (id: string, reason: string): Promise<SalaryTaxDeposit> =>
        (await axios.post(ENDPOINTS.SALARY_TAX_DEPOSIT.REVERSE(id), { reason }, headers())).data,

    // ---- statutory remittances
    listRemittances: async (institutionId: string, kind?: RemittanceKind, year?: number): Promise<StatutoryRemittance[]> =>
        (await axios.get(ENDPOINTS.STATUTORY_REMITTANCE.LIST(institutionId), { ...headers(), params: { kind, year } })).data,
    remittanceSuggestion: async (institutionId: string, kind: RemittanceKind, year: number, month?: number): Promise<RemittanceSuggestion> =>
        (await axios.get(ENDPOINTS.STATUTORY_REMITTANCE.SUGGESTION(institutionId), { ...headers(), params: { kind, year, month } })).data,
    createRemittance: async (institutionId: string, req: StatutoryRemittanceRequest): Promise<StatutoryRemittance> =>
        (await axios.post(ENDPOINTS.STATUTORY_REMITTANCE.CREATE(institutionId), req, headers())).data,
    reverseRemittance: async (id: string, reason: string): Promise<StatutoryRemittance> =>
        (await axios.post(ENDPOINTS.STATUTORY_REMITTANCE.REVERSE(id), { reason }, headers())).data,

    // ---- payroll funding check
    fundingCheck: async (institutionId: string, bankAccountId: string | null, payrollIds: string[]): Promise<FundingCheck> =>
        (await axios.post(ENDPOINTS.PAYROLL_FUNDING_CHECK.CHECK(institutionId), { bankAccountId, payrollIds }, headers())).data,

    // ---- close checklist
    listTemplates: async (institutionId: string): Promise<TemplateTask[]> =>
        (await axios.get(ENDPOINTS.CLOSE_CHECKLIST.TEMPLATES(institutionId), headers())).data,
    loadChecklistDefaults: async (institutionId: string): Promise<LoadDefaultsResult> =>
        (await axios.post(ENDPOINTS.CLOSE_CHECKLIST.LOAD_DEFAULTS(institutionId), null, headers())).data,
    createTemplate: async (institutionId: string, req: TemplateTaskRequest): Promise<TemplateTask> =>
        (await axios.post(ENDPOINTS.CLOSE_CHECKLIST.TEMPLATES(institutionId), req, headers())).data,
    updateTemplate: async (id: string, req: TemplateTaskRequest): Promise<TemplateTask> =>
        (await axios.put(ENDPOINTS.CLOSE_CHECKLIST.TEMPLATE(id), req, headers())).data,
    deleteTemplate: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.CLOSE_CHECKLIST.TEMPLATE(id), headers());
    },
    listPeriods: async (institutionId: string, year?: number): Promise<ClosePeriod[]> =>
        (await axios.get(ENDPOINTS.CLOSE_CHECKLIST.PERIODS(institutionId, year), headers())).data,
    getPeriod: async (id: string): Promise<ClosePeriod> =>
        (await axios.get(ENDPOINTS.CLOSE_CHECKLIST.PERIOD(id), headers())).data,
    openPeriod: async (institutionId: string, year: number, month: number): Promise<ClosePeriod> =>
        (await axios.post(ENDPOINTS.CLOSE_CHECKLIST.PERIODS(institutionId), { year, month }, headers())).data,
    setCloseTaskStatus: async (taskId: string, status: TaskStatus, note?: string): Promise<ClosePeriod> =>
        (await axios.put(ENDPOINTS.CLOSE_CHECKLIST.TASK_STATUS(taskId), { status, note: note || null }, headers())).data,
    setCloseTaskOwner: async (taskId: string, ownerUserId: string | null): Promise<ClosePeriod> =>
        (await axios.put(ENDPOINTS.CLOSE_CHECKLIST.TASK_OWNER(taskId), { ownerUserId }, headers())).data,
    signOffPeriod: async (id: string): Promise<ClosePeriod> =>
        (await axios.post(ENDPOINTS.CLOSE_CHECKLIST.SIGN_OFF(id), null, headers())).data,
    reopenPeriod: async (id: string, reason: string): Promise<ClosePeriod> =>
        (await axios.post(ENDPOINTS.CLOSE_CHECKLIST.REOPEN(id), { reason }, headers())).data,
    getGate: async (institutionId: string): Promise<{ requireCloseChecklist: boolean }> =>
        (await axios.get(ENDPOINTS.CLOSE_CHECKLIST.GATE(institutionId), headers())).data,
    setGate: async (institutionId: string, requireCloseChecklist: boolean): Promise<{ requireCloseChecklist: boolean }> =>
        (await axios.put(ENDPOINTS.CLOSE_CHECKLIST.GATE(institutionId), { requireCloseChecklist }, headers())).data,

    // ---- filing calendar
    listObligations: async (institutionId: string): Promise<Obligation[]> =>
        (await axios.get(ENDPOINTS.COMPLIANCE.OBLIGATIONS(institutionId), headers())).data,
    loadObligationDefaults: async (institutionId: string): Promise<LoadDefaultsResult> =>
        (await axios.post(ENDPOINTS.COMPLIANCE.LOAD_DEFAULTS(institutionId), null, headers())).data,
    createObligation: async (institutionId: string, req: ObligationRequest): Promise<Obligation> =>
        (await axios.post(ENDPOINTS.COMPLIANCE.OBLIGATIONS(institutionId), req, headers())).data,
    updateObligation: async (id: string, req: ObligationRequest): Promise<Obligation> =>
        (await axios.put(ENDPOINTS.COMPLIANCE.OBLIGATION(id), req, headers())).data,
    deleteObligation: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.COMPLIANCE.OBLIGATION(id), headers());
    },
    listTasks: async (institutionId: string, status?: TaskStatus, from?: string, to?: string): Promise<ComplianceTask[]> =>
        (await axios.get(ENDPOINTS.COMPLIANCE.TASKS(institutionId), { ...headers(), params: { status, from, to } })).data,
    updateTask: async (id: string, status: TaskStatus, note?: string, linkedDocumentType?: string, linkedDocumentId?: string): Promise<ComplianceTask> =>
        (await axios.put(ENDPOINTS.COMPLIANCE.TASK(id), {
            status, note: note || null, linkedDocumentType: linkedDocumentType || null, linkedDocumentId: linkedDocumentId || null,
        }, headers())).data,
};
